import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './modules/components/Layout';
import { AuthProvider } from './modules/auth/AuthContext';
import { ProtectedRoute } from './modules/auth/ProtectedRoute';
import { AdminSettingsProvider } from './modules/store/admin-settings.store';
import LoginPage from './modules/pages/LoginPage';
import DashboardPage from './modules/pages/DashboardPage';
import InvitationsPage from './modules/pages/InvitationsPage';
import TemplatesPage from './modules/pages/TemplatesPage';
import FeedbackListPage from './modules/pages/feedback/FeedbackListPage';
import FeedbackDetailPage from './modules/pages/feedback/FeedbackDetailPage';
import UsersPage from './modules/pages/admin/users/UsersPage';
import UserDetailPage from './modules/pages/admin/users/UserDetailPage';
import SupportDashboardPage from './modules/pages/admin/support/SupportDashboardPage';
import SupportTicketDetailPage from './modules/pages/admin/support/SupportTicketDetailPage';
import FaqAdminPage from './modules/pages/admin/support/FaqAdminPage';
import { TypeBStudioPage } from './modules/pages/TypeBStudioPage';
import { initAnalytics } from './infrastructure/lib/analytics';
import { AnalyticsTracker } from './infrastructure/shared/analytics/AnalyticsTracker';

// Initialize analytics dynamically when VITE_GA_MEASUREMENT_ID is present
initAnalytics();

const SettingsPage = lazy(() => import('./modules/pages/admin/settings/SettingsPage'));
const NotificationsPage = lazy(() => import('./modules/pages/notifications/NotificationsPage'));
const AnalyticsPage = lazy(() => import('./modules/pages/AnalyticsPage'));
const MediaPage = lazy(() => import('./modules/pages/MediaPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AdminSettingsProvider>
          <BrowserRouter>
            <AnalyticsTracker />
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/invitations" element={<InvitationsPage />} />
                  <Route path="/templates" element={<TemplatesPage />} />
                  <Route path="/type-b" element={<TypeBStudioPage />} />
                  <Route path="/feedback" element={<FeedbackListPage />} />
                  <Route path="/feedback/:id" element={<FeedbackDetailPage />} />
                  <Route path="/support" element={<SupportDashboardPage />} />
                  <Route path="/support/faqs" element={<FaqAdminPage />} />
                  <Route path="/support/:id" element={<SupportTicketDetailPage />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/users/:id" element={<UserDetailPage />} />

                  <Route
                    path="/media"
                    element={
                      <Suspense fallback={<div className="p-10 text-on-surface-variant">Loading…</div>}>
                        <MediaPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <Suspense fallback={<div className="p-10 text-on-surface-variant">Loading…</div>}>
                        <NotificationsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <Suspense fallback={<div className="p-10 text-on-surface-variant">Loading…</div>}>
                        <AnalyticsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <Suspense fallback={<div className="p-10 text-on-surface-variant">Loading…</div>}>
                        <SettingsPage />
                      </Suspense>
                    }
                  />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </BrowserRouter>
        </AdminSettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
